import { Badge } from "components/ui/badge";
import { Combobox } from "components/ui/combobox";
import type { TimeZoneOption } from "helpers";

type TimeZoneComboboxProps = {
  ariaLabel: string;
  filterOptions: (
    options: TimeZoneOption[],
    inputValue: string,
  ) => TimeZoneOption[];
  id: string;
  inputValue: string;
  onClear: () => void;
  onInputValueChange: (inputValue: string) => void;
  onSelectOption: (option: TimeZoneOption) => void;
  options: TimeZoneOption[];
  placeholder: string;
  value: TimeZoneOption | null;
};

const getTimeZoneOptionKey = (option: TimeZoneOption) =>
  `${option.isCustomAction ? "custom" : "option"}:${option.value}`;

export function TimeZoneCombobox({
  ariaLabel,
  filterOptions,
  id,
  inputValue,
  onClear,
  onInputValueChange,
  onSelectOption,
  options,
  placeholder,
  value,
}: TimeZoneComboboxProps) {
  return (
    <Combobox
      ariaLabel={ariaLabel}
      className="w-full"
      emptyMessage="No timezones match your search"
      filterOptions={(availableOptions, search) =>
        filterOptions([...availableOptions], search)
      }
      getOptionKey={getTimeZoneOptionKey}
      getOptionLabel={(option) => option.value}
      id={id}
      inputValue={inputValue}
      isOptionEqualToValue={(option, selectedOption) =>
        option.value === selectedOption.value &&
        !!option.isCustomAction === !!selectedOption.isCustomAction
      }
      onClear={onClear}
      onInputValueChange={onInputValueChange}
      onSelectOption={onSelectOption}
      options={options}
      placeholder={placeholder}
      renderOption={(option) =>
        option.isCustomAction ? (
          <span>Can&apos;t find timezone? Add custom: {option.value}</span>
        ) : (
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
            <span className="truncate">{option.value}</span>
            <span className="inline-flex shrink-0 gap-1">
              {option.isInUse ? (
                <Badge className="border-emerald-300 bg-emerald-100 text-emerald-800" variant="outline">
                  In Use
                </Badge>
              ) : null}
              {option.isPreferred ? (
                <Badge variant="secondary">Preferred</Badge>
              ) : null}
            </span>
          </div>
        )
      }
      value={value}
    />
  );
}